import { useState } from 'react';
import { AtSign, Camera, Feather, MailCheck, PenLine, Send, Smile } from 'lucide-react';
import { useHideGettingStartedMutation, useMeQuery, type GettingStarted } from '../profiles/api';
import { InviteChoices } from './InviteChoices';
import { ShareKitSheet } from './ShareKit';
import { useResendVerification } from './verify';

/**
 * "Getting started" (onboarding plan, slice 2): at the top of the letters
 * list, or in the side panel on wide screens. It lists only what is still
 * undone — no counts, no percentages, nothing owed — and the server puts it
 * away for good once the first letter is sealed and two more are done, or
 * when "Hide this" is pressed.
 */

export type Item = keyof GettingStarted;

export const ITEMS: { key: Item; title: string; note: string; Icon: typeof Camera }[] = [
  { key: 'photo', title: 'Add a photo', note: 'So people know whose letter it is.', Icon: Camera },
  { key: 'bio', title: 'Write a line about you', note: 'A little hello for whoever opens your letters.', Icon: Smile },
  { key: 'invited', title: 'Invite someone', note: "Send your toiyp to someone you'd love to hear from.", Icon: Send },
  { key: 'bioShared', title: 'Put your toiyp in your bio', note: 'So people who find you online can write.', Icon: AtSign },
  { key: 'emailConfirmed', title: 'Confirm your email', note: 'Then friends you bring earn you both a welcome stamp.', Icon: MailCheck },
  { key: 'firstLetter', title: 'Write your first letter', note: "Somebody's letterbox is waiting.", Icon: PenLine },
];

/** The undone items, in order. Pure, for the tests. */
export function undoneItems(done: GettingStarted): Item[] {
  return ITEMS.map((i) => i.key).filter((k) => !done[k]);
}

export function GettingStartedCard({
  onOpenProfile,
  onWrite,
}: {
  onOpenProfile: () => void;
  /** Opens "Write to someone new". */
  onWrite: () => void;
}) {
  const { data: me } = useMeQuery();
  const [hide, { isLoading: hiding }] = useHideGettingStartedMutation();
  const [inviting, setInviting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const resend = useResendVerification();
  const done = me?.gettingStarted;
  if (!me?.handle || !done) return null;
  const undone = undoneItems(done);
  if (undone.length === 0) return null;

  const act = (key: Item) => {
    if (key === 'photo' || key === 'bio') onOpenProfile();
    else if (key === 'invited') setInviting((v) => !v);
    else if (key === 'bioShared') setSharing(true);
    else if (key === 'emailConfirmed') void resend.send();
    else onWrite();
  };

  return (
    <section className="getting-started" aria-labelledby="getting-started-title">
      <div className="getting-started__head">
        <span className="getting-started__icon" aria-hidden="true">
          <Feather size={16} />
        </span>
        <div>
          <h2 className="getting-started__title" id="getting-started-title">
            Getting started
          </h2>
          <p className="getting-started__lead">A few things that help letters find you.</p>
        </div>
      </div>

      <ul className="getting-started__list">
        {ITEMS.filter((i) => undone.includes(i.key)).map(({ key, title, note, Icon }) => (
          <li key={key}>
            <button
              type="button"
              className="getting-started__item"
              aria-expanded={key === 'invited' ? inviting : undefined}
              onClick={() => act(key)}
            >
              <span className="getting-started__item-icon" aria-hidden="true">
                <Icon size={16} />
              </span>
              <span className="getting-started__item-text">
                <span className="getting-started__item-title">{title}</span>
                <span className="getting-started__item-note">{note}</span>
              </span>
            </button>
            {key === 'invited' && inviting && (
              <div className="getting-started__invite">
                <InviteChoices
                  handle={me.handle!}
                  onStarted={(id) => {
                    window.location.hash = `#/c/${id}`;
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>

      {resend.message && (
        <p className="getting-started__sent" role="status">
          {resend.message}
        </p>
      )}
      {sharing && <ShareKitSheet from="getting_started" onClose={() => setSharing(false)} />}
      <button type="button" className="linkish getting-started__hide" disabled={hiding} onClick={() => void hide()}>
        Hide this
      </button>
    </section>
  );
}
